import React from 'react';
import { SkinProps } from '../../../../types/component-skin';
import { formatCurrency } from '../../../../utils/formatUtils';

/**
 * 커스텀 멤버십 가입 기본 스킨
 */
const BasicCustomMembershipSignupSkin: React.FC<SkinProps> = ({ 
  data, 
  actions, 
  options, 
  mode, 
  utils 
}) => {
  const { t } = utils;
  
  // 데이터 추출
  const { 
    isRegistered,
    isChecking,
    isUserLoggedIn,
    productInfo,
    selectedAmount,
    selectedQuantity,
    selectedPriceId,
    additionalProducts,
    selectedAdditionalProducts,
    loading,
    assetBalance,
    theme,
    askAmount,
    askQuantity,
    askAdditionalProducts,
    minAmount,
    maxAmount,
    minQuantity,
    maxQuantity
  } = data;
  
  // 액션 추출
  const {
    handleMembershipSignup,
    handleAmountChange,
    handleQuantityChange,
    handleAdditionalProductToggle,
    formatNumber
  } = actions;
  
  // 옵션 추출
  const {
    title = t('커스텀 멤버십 가입'),
    buttonText = t('가입 신청'),
    completedText = t('가입 완료'),
    description = t('원하는 옵션을 선택하여 멤버십에 가입하세요.'),
    buttonColor = '#007bff',
    titleColor = '#333333',
    descriptionColor = '#666666'
  } = options;
  
  // 스타일 정의
  const containerStyle: React.CSSProperties = {
    maxWidth: '600px',
    margin: '0 auto',
    padding: '40px 20px'
  };
  
  const titleStyle: React.CSSProperties = {
    fontSize: '32px',
    fontWeight: 'bold',
    color: titleColor,
    marginBottom: '20px',
    textAlign: 'center' as const
  };
  
  const descriptionStyle: React.CSSProperties = {
    fontSize: '16px',
    color: descriptionColor,
    lineHeight: '1.6',
    marginBottom: '40px',
    textAlign: 'center' as const
  };
  
  const formSectionStyle: React.CSSProperties = {
    marginBottom: '30px',
    padding: '20px',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px'
  };
  
  const labelStyle: React.CSSProperties = {
    display: 'block',
    marginBottom: '8px',
    fontSize: '14px',
    fontWeight: '500',
    color: '#333'
  };
  
  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '12px 16px',
    fontSize: '16px',
    border: '1px solid #ddd',
    borderRadius: '6px',
    backgroundColor: '#fff',
    boxSizing: 'border-box' as const
  };
  
  const buttonStyle: React.CSSProperties = {
    width: '100%',
    padding: '16px 40px',
    fontSize: '18px',
    fontWeight: 'bold',
    color: '#fff',
    backgroundColor: buttonColor,
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    opacity: loading ? 0.6 : 1
  };
  
  const completedStyle: React.CSSProperties = {
    padding: '20px',
    backgroundColor: '#d4edda',
    color: '#155724',
    borderRadius: '8px',
    fontSize: '18px',
    fontWeight: 'bold',
    textAlign: 'center' as const
  };
  
  const loadingStyle: React.CSSProperties = {
    padding: '40px',
    fontSize: '16px',
    color: '#666',
    textAlign: 'center' as const
  };
  
  const loginPromptStyle: React.CSSProperties = {
    padding: '40px',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    marginTop: '20px',
    textAlign: 'center' as const
  };
  
  const assetBalanceStyle: React.CSSProperties = {
    padding: '15px',
    backgroundColor: '#e7f3ff',
    borderRadius: '6px',
    marginBottom: '20px',
    fontSize: '14px',
    color: '#0066cc'
  };
  
  const additionalProductStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    padding: '12px',
    marginBottom: '10px',
    backgroundColor: '#fff',
    border: '1px solid #ddd',
    borderRadius: '6px',
    cursor: 'pointer'
  };
  
  // 로딩 중일 때
  if (isChecking) {
    return (
      <div style={containerStyle}>
        <div style={loadingStyle}>
          {t('멤버십 상태를 확인하고 있습니다...')}
        </div>
      </div>
    );
  }
  
  // 이미 등록된 경우
  if (isRegistered) {
    return (
      <div style={containerStyle}>
        <h2 style={titleStyle}>{title}</h2>
        <div style={completedStyle}>
          ✓ {completedText}
        </div>
        <p style={{ marginTop: '20px', color: descriptionColor, textAlign: 'center' }}>
          {t('이미 멤버십에 가입되어 있습니다.')}
        </p>
      </div>
    );
  }
  
  // 로그인되지 않은 경우
  if (!isUserLoggedIn) {
    return (
      <div style={containerStyle}>
        <h2 style={titleStyle}>{title}</h2>
        <p style={descriptionStyle}>{description}</p>
        <div style={loginPromptStyle}>
          <p style={{ marginBottom: '20px', fontSize: '16px' }}>
            {t('멤버십 가입을 위해 로그인이 필요합니다.')}
          </p>
          <button
            style={buttonStyle}
            onClick={() => utils.navigate && utils.navigate('/login')}
          >
            {t('로그인하기')}
          </button>
        </div>
      </div>
    );
  }
  
  // 정상적인 가입 폼
  return (
    <div style={containerStyle}>
      <h2 style={titleStyle}>{title}</h2>
      <p style={descriptionStyle}>{description}</p>
      
      {/* 자산 잔액 표시 */}
      {assetBalance > 0 && (
        <div style={assetBalanceStyle}>
          {t('사용 가능한 자산')}: {formatCurrency(assetBalance)}
        </div>
      )}
      
      {/* 금액 입력 */}
      {askAmount && (
        <div style={formSectionStyle}>
          <label style={labelStyle}>
            {t('금액')} ({t('최소')}: {formatCurrency(minAmount)}, {t('최대')}: {formatCurrency(maxAmount)})
          </label>
          <input
            type="text"
            value={formatNumber(selectedAmount.toString())}
            onChange={(e) => handleAmountChange(e.target.value)}
            style={inputStyle}
            placeholder={t('금액을 입력하세요')}
          />
        </div>
      )}
      
      {/* 수량 입력 */}
      {askQuantity && (
        <div style={formSectionStyle}>
          <label style={labelStyle}>
            {t('수량')} ({t('최소')}: {minQuantity}, {t('최대')}: {maxQuantity})
          </label>
          <input
            type="text"
            value={selectedQuantity.toString()}
            onChange={(e) => handleQuantityChange(e.target.value)}
            style={inputStyle}
            placeholder={t('수량을 입력하세요')}
          />
        </div>
      )}
      
      {/* 추가 상품 선택 */}
      {askAdditionalProducts && additionalProducts.length > 0 && (
        <div style={formSectionStyle}>
          <label style={labelStyle}>
            {t('추가 상품 선택')}
          </label>
          {additionalProducts.map((product: any) => (
            <div
              key={product.id}
              style={{
                ...additionalProductStyle,
                borderColor: selectedAdditionalProducts.includes(product.id) ? buttonColor : '#ddd',
                backgroundColor: selectedAdditionalProducts.includes(product.id) ? `${buttonColor}10` : '#fff'
              }}
              onClick={() => handleAdditionalProductToggle(product.id)}
            >
              <input
                type="checkbox"
                checked={selectedAdditionalProducts.includes(product.id)}
                onChange={() => {}}
                style={{ marginRight: '12px' }}
              />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 'bold' }}>{product.name}</div>
                {product.price && (
                  <div style={{ fontSize: '14px', color: '#666' }}>
                    {formatCurrency(product.price)}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* 총 금액 표시 */}
      {(askAmount || askQuantity) && (
        <div style={{ 
          textAlign: 'center' as const, 
          marginBottom: '30px',
          fontSize: '24px',
          fontWeight: 'bold',
          color: titleColor
        }}>
          {t('총 금액')}: {formatCurrency(selectedAmount * selectedQuantity)}
        </div>
      )}
      
      {/* 가입 버튼 */}
      <button
        style={buttonStyle}
        onClick={handleMembershipSignup}
        disabled={loading}
        onMouseEnter={(e) => {
          if (!loading) {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 6px 20px rgba(0, 0, 0, 0.15)';
          }
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = 'none';
        }}
      >
        {loading ? t('처리 중...') : buttonText}
      </button>
    </div>
  );
};

export default BasicCustomMembershipSignupSkin;